import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import LineChart from "@galaxy-io/dls/charts/LineChart";
import StatChart, { StatChartVariant } from "@galaxy-io/dls/charts/StatChart";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import Box from "@galaxy-io/dls/layout/Box";
import InfiniteTable from "@galaxy-io/dls/table/InfiniteTable";
import Text, { TextSize, TextWeight } from "@galaxy-io/dls/text/Text";
import Widget from "@galaxy-io/dls/widget/Widget";
import type {
  GithubMetrics,
  MetricResult,
  Traffic,
} from "@gtm/contracts/integrations/github-metrics";
import { useQuery } from "@tanstack/react-query";
import { Link, useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { Inset } from "@/components/workspace/people-styles";
import { SelectField } from "@/components/workspace/select-field";
import { Toolbar } from "@/components/workspace/toolbar";
import { apiClient, unwrap } from "@/lib/api-client";
import { Cards, Dashboard, Grid, Plot, TableBody } from "./dashboard-styles";

export function GithubMetricsView() {
  const { params, update } = useUrlState();
  const sources = useQuery({
    queryKey: ["github-repositories"],
    queryFn: () => unwrap(apiClient().GET("/integrations/github/repositories", {})),
  });
  const available = sources.data?.filter((s) => s.enabled) ?? [];
  const requested = params.get("githubSource");
  const selected = requested
    ? available.find((s) => s.id === requested)
    : (available.find((s) => s.fullName.split("/")[1]?.toLowerCase() === "filament") ??
      available[0]);
  const metrics = useQuery({
    queryKey: ["github-metrics", selected?.id],
    enabled: !!selected,
    queryFn: () =>
      unwrap(
        apiClient().GET("/reporting/github", { params: { query: { sourceId: selected!.id } } }),
      ),
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
  });
  if (!sources.data)
    return (
      <QueryState
        error={sources.error}
        loadingMessage="Loading GitHub repositories…"
        onRetry={() => void sources.refetch()}
      />
    );
  if (!available.length)
    return (
      <Inset>
        <Text>No active tracked GitHub repositories.</Text>
        <Link href="/integrations">Connect GitHub and track Filament in Integrations</Link>
      </Inset>
    );
  return (
    <>
      <Toolbar>
        <SelectField
          compact
          label="Repository"
          value={selected?.id ?? ""}
          placeholder="Choose a repository"
          width={260}
          options={available.map((s) => ({ value: s.id, label: s.fullName }))}
          onChange={(githubSource) => update({ githubSource })}
          hideLabel
        />
        <Button
          label="Refresh"
          variant={ButtonVariant.SECONDARY}
          isDisabled={!selected || metrics.isFetching}
          onClick={() => void metrics.refetch()}
          size={ButtonSize.SMALL}
        />
      </Toolbar>
      {!selected ? (
        <Inset>
          <Text>Choose an available repository.</Text>
        </Inset>
      ) : !metrics.data ? (
        <QueryState
          error={metrics.error}
          loadingMessage="Loading GitHub metrics…"
          onRetry={() => void metrics.refetch()}
        />
      ) : (
        <>
          {metrics.error && (
            <QueryState error={metrics.error} onRetry={() => void metrics.refetch()} />
          )}
          <Content data={metrics.data} />
        </>
      )}
    </>
  );
}
function TrafficCard({ title, result }: { title: string; result: MetricResult<Traffic> }) {
  return (
    <Widget
      isFlush
      header={
        <Text weight={TextWeight.MEDIUM} as="h2">
          {title} · last 14 days
        </Text>
      }
    >
      <Plot>
        {result.data ? (
          <>
            <Text>
              {result.data.count.toLocaleString()} total · {result.data.uniques.toLocaleString()}{" "}
              unique
            </Text>
            <Box height={200}>
              <LineChart
                isFilterable
                ariaLabel={title}
                hasLegend
                series={{ total: { label: title }, unique: { label: "Unique" } }}
                labelFormatter={(day) => day.slice(5, 10)}
                lines={[
                  {
                    metric: "total",
                    points: result.data.days.map((d) => ({ x: d.timestamp, y: d.count })),
                  },
                  {
                    metric: "unique",
                    points: result.data.days.map((d) => ({ x: d.timestamp, y: d.uniques })),
                  },
                ]}
              />
            </Box>
          </>
        ) : (
          <Text>{result.error}</Text>
        )}
      </Plot>
    </Widget>
  );
}
function Breakdown({
  title,
  error,
  rows,
  metric = "Views",
}: {
  title: string;
  error: string | null;
  rows: { label: string; count: number; uniques?: number }[];
  metric?: string;
}) {
  return (
    <Widget
      isFlush
      header={
        <Text weight={TextWeight.MEDIUM} as="h2">
          {title}
        </Text>
      }
    >
      {error ? (
        <Plot>
          <Alert variant={AlertVariant.ERROR}>{error}</Alert>
        </Plot>
      ) : !rows.length ? (
        <Plot>
          <Text>No data reported by GitHub.</Text>
        </Plot>
      ) : (
        <TableBody>
          <Box height="100%">
            <InfiniteTable
              ariaLabel={title}
              data={rows}
              getRowId={(r) => r.label}
              columns={[
                { id: "label", header: title, accessor: (r) => r.label, isRowHeader: true },
                { id: "count", header: metric, accessor: (r) => r.count, align: "right" as const },
                ...(metric === "Views"
                  ? [
                      {
                        id: "uniques",
                        header: "Unique visitors",
                        accessor: (r: { uniques?: number }) => r.uniques,
                        align: "right" as const,
                      },
                    ]
                  : []),
              ]}
            />
          </Box>
        </TableBody>
      )}
    </Widget>
  );
}
function Content({ data }: { data: GithubMetrics }) {
  const summary = data.summary.data;
  const stats = [
    ["Stars", summary?.stars],
    ["Forks", summary?.forks],
    ["Watchers", summary?.watchers],
    ["Open issues & PRs", summary?.openIssuesAndPullRequests],
    ["Contributors", data.contributors.data?.length],
  ] as const;
  return (
    <Dashboard>
      <Text size={TextSize.BODY_SM}>
        {data.repository} · Fetched {new Date(data.fetchedAt).toLocaleString()} · Refreshes every
        minute. GitHub data can lag; traffic covers the last 14 days (UTC).
      </Text>
      <Cards>
        {stats.map(([label, value]) => (
          <StatChart
            hasBorder
            key={label}
            label={label}
            variant={StatChartVariant.BASE}
            value={
              <Text size={TextSize.HEADING_MD} weight={TextWeight.MEDIUM}>
                {value?.toLocaleString() ?? "—"}
              </Text>
            }
          />
        ))}
      </Cards>
      {data.summary.error && <Text>{data.summary.error}</Text>}
      <Grid>
        <TrafficCard title="Views" result={data.views} />
        <TrafficCard title="Clones" result={data.clones} />
        <Breakdown
          title="Referring sites · last 14 days"
          error={data.referrers.error}
          rows={(data.referrers.data ?? []).map((r) => ({
            label: r.referrer,
            count: r.count,
            uniques: r.uniques,
          }))}
        />
        <Breakdown
          title="Popular pages · last 14 days"
          error={data.paths.error}
          rows={(data.paths.data ?? []).map((r) => ({
            label: r.path,
            count: r.count,
            uniques: r.uniques,
          }))}
        />
      </Grid>
      <Breakdown
        title="Contributors · default branch"
        metric="Commits"
        error={data.contributors.error}
        rows={(data.contributors.data ?? []).map((r) => ({
          label: r.login,
          count: r.contributions,
        }))}
      />
      <Text size={TextSize.BODY_SM}>
        Contributors reflect GitHub’s cached commit attribution and exclude anonymous contributors.
        Traffic history older than 14 days is not retained here.
      </Text>
    </Dashboard>
  );
}
